import { type StoryblokAlgoliaCategorySearchFilterGroupType } from '../../storyblok'

import {
  conditionToClause,
  conditionToOrPack,
} from './storyblokAlgoliaCategorySearchFiltersConditions'
import {
  joinWithAnd,
  orSameAttribute,
} from './storyblokAlgoliaCategorySearchFiltersText'
import {
  type FilterNode,
  type OrGroupPack,
} from './storyblokAlgoliaCategorySearchFiltersTypes'

const toOrGroupPack = (node: FilterNode): OrGroupPack | null => {
  if (node.component !== 'AlgoliaCategorySearchFilterCondition') {
    return null
  }

  return conditionToOrPack(node)
}

const collectOrGroupPacks = (
  children: FilterNode[],
): Exclude<OrGroupPack, null>[] => {
  const packs: Exclude<OrGroupPack, null>[] = []

  for (const child of children) {
    const pack = toOrGroupPack(child)
    if (pack) {
      packs.push(pack)
    }
  }

  return packs
}

const getGroupChildren = (
  group: StoryblokAlgoliaCategorySearchFilterGroupType,
): FilterNode[] => group.conditionsInThisGroup ?? []

const buildOrGroupClause = (children: FilterNode[]): string | null => {
  const orGroupPacks = collectOrGroupPacks(children)

  if (orGroupPacks.length === 0 || orGroupPacks.length !== children.length) {
    return null
  }

  const attributes = [...new Set(orGroupPacks.map((pack) => pack.attribute))]
  if (attributes.length !== 1) {
    return null
  }

  const uniqueValues = [...new Set(orGroupPacks.flatMap((pack) => pack.values))]
  const mergedClause = orSameAttribute(
    attributes[0],
    uniqueValues,
    orGroupPacks[0].operator,
  )

  return mergedClause || null
}

const buildFallbackOrClauses = (
  children: FilterNode[],
  buildClauses: (node: FilterNode) => string[],
): string[] => {
  const fallbackClauses: string[] = []

  for (const child of children) {
    const clauses = buildClauses(child)
    if (clauses.length === 0) {
      // eslint-disable-next-line no-continue -- don't reference, refactor if touched
      continue
    }
    if (clauses.length === 1) {
      fallbackClauses.push(clauses[0])
      // eslint-disable-next-line no-continue -- don't reference, refactor if touched
      continue
    }

    fallbackClauses.push(joinWithAnd(clauses))
  }

  if (fallbackClauses.length === 0) {
    return []
  }
  if (fallbackClauses.length === 1) {
    return fallbackClauses
  }

  return [`(${fallbackClauses.join(' OR ')})`]
}

const nodeToClauses = (node: FilterNode): string[] => {
  if (node.component === 'AlgoliaCategorySearchFilterCondition') {
    const clause = conditionToClause(node)

    return clause ? [clause] : []
  }

  const groupLogic = node.groupLogic === 'or' ? 'or' : 'and'
  const children = getGroupChildren(node)
  const childClauses = children.flatMap((child) => nodeToClauses(child))

  if (groupLogic === 'and') {
    return childClauses
  }

  const mergedClause = buildOrGroupClause(children)
  if (mergedClause) {
    return [mergedClause]
  }

  return buildFallbackOrClauses(children, nodeToClauses)
}

export { nodeToClauses }
